/**
 * One GST invoice. A full-access user gets the editor; a view-only user gets
 * the same record as a read-only sheet with the PDF download — and is sent
 * back to the list if they try to reach the "new invoice" screen by URL.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import GstInvoiceForm from './GstInvoiceForm';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';
import { useInvoiceAccess, ViewOnlyTag } from '../../../lib/invoiceAccess';

const Row = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm text-slate-800">{value || '—'}</div>
  </div>
);

const ReadOnlyInvoice = ({ id }) => {
  const { authHeader } = useAuth();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/gst/invoices/${id}`, { headers: authHeader() });
      setInv(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-12 flex justify-center text-slate-400" data-testid="gst-view-loading">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (err || !inv) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
          {err || 'Invoice not found'}
        </div>
      </div>
    );
  }

  const cust = inv.customer_snapshot || {};
  const veh = inv.vehicle || {};
  const t = inv.totals || {};
  const paid = Number(inv.amount_paid || 0);
  const balance = Math.max(0, Number(t.grand_total || 0) - paid);

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="gst-invoice-view">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/admin/gst/invoices" className="text-slate-500 hover:text-slate-800" data-testid="gst-view-back">
          <ArrowLeft size={18} />
        </Link>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-mono">{inv.number}</h1>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold bg-slate-100 text-slate-600">
          {inv.status}
        </span>
        <ViewOnlyTag />
        <a
          href={`${API}/gst/invoices/${id}/pdf`} target="_blank" rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1.5 text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg"
          data-testid="gst-view-pdf-btn"
        >
          <Download size={14} /> Download PDF
        </a>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 grid gap-4 sm:grid-cols-3">
        <Row label="Customer" value={[cust.prefix, cust.name].filter(Boolean).join(' ')} />
        <Row label="GSTIN" value={cust.gstin || 'B2C'} />
        <Row label="Phone" value={cust.phone} />
        <Row label="Invoice date" value={inv.invoice_date} />
        <Row label="Place of supply" value={inv.place_of_supply} />
        <Row label="Vehicle" value={[veh.make, veh.model, veh.registration].filter(Boolean).join(' · ')} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left">
            <tr>
              <th className="px-3 py-2 font-semibold text-slate-600">Item</th>
              <th className="px-3 py-2 font-semibold text-slate-600">HSN/SAC</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Qty</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Rate</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">GST %</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Taxable</th>
              <th className="px-3 py-2 font-semibold text-slate-600 text-right">Tax</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(inv.items || []).map((it, n) => (
              <tr key={it.id || n} data-testid={`gst-view-item-${n}`}>
                <td className="px-3 py-2">
                  <div className="text-slate-800">{it.name}</div>
                  {!!it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{it.hsn_code || '—'}</td>
                <td className="px-3 py-2 text-right font-mono">{it.qty} {it.unit}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(it.rate)}</td>
                <td className="px-3 py-2 text-right font-mono">{it.gst_rate}%</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(it.taxable_value)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-700">
                  {fmtINR((it.cgst_amount || 0) + (it.sgst_amount || 0) + (it.igst_amount || 0))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-1.5 text-sm" data-testid="gst-view-totals">
          {[
            ['Taxable value', t.taxable_value],
            ['CGST', t.cgst_amount], ['SGST', t.sgst_amount],
            ['IGST', t.igst_amount], ['Cess', t.cess_amount],
            ['Round off', t.round_off],
          ].filter(([, v]) => Number(v || 0) !== 0).map(([label, v]) => (
            <div key={label} className="flex justify-between">
              <span className="text-slate-500">{label}</span>
              <span className="font-mono text-slate-800">{fmtINR(v)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-slate-200 pt-1.5 font-semibold">
            <span className="text-slate-700">Grand total</span>
            <span className="font-mono text-teal-700">{fmtINR(t.grand_total)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Paid</span>
            <span className="font-mono text-emerald-700">{fmtINR(paid)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Balance</span>
            <span className={`font-mono ${balance > 0 ? 'text-red-600' : 'text-slate-800'}`}>{fmtINR(balance)}</span>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm space-y-2">
          <Row label="Notes" value={inv.notes} />
          <Row label="Terms" value={inv.terms} />
          <p className="text-xs text-slate-400 flex items-center gap-1.5 pt-1">
            <ShieldCheck size={13} /> You have view-only access to GST invoicing, so this
            record cannot be changed here.
          </p>
        </div>
      </div>
    </div>
  );
};

const GstInvoiceRoute = () => {
  const { id } = useParams();
  const { canEdit } = useInvoiceAccess();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canEdit && !id) {
      toast.error("You don't have permission to create GST invoices");
      navigate('/admin/gst/invoices', { replace: true });
    }
  }, [canEdit, id, navigate]);

  if (canEdit) return <GstInvoiceForm />;
  if (!id) return null;
  return <ReadOnlyInvoice id={id} />;
};

export default GstInvoiceRoute;
